import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import axios from "axios";
import Button from "../common/Button";

// One add-on's seats on the staff Edit Agency modal: the seat count with
// its source and period, and the pool with a PO/comp hour top-up form.
// Rendered once per add-on (Investigations, Footage analysis); both hit
// the same /addons/:addon staff routes. Seat edits are staged and written
// by the modal's Save through the ref; hour credits post immediately.

export type AddonKey = "investigations" | "video";
type Source = "invoice" | "comp" | "trial";
type Interval = "month" | "year";
type Pool = { minutesUsed: number; allowanceMinutes: number; creditMinutes: number; pct: number; period: { interval: Interval; label: string } };

export type AddonSeatsSectionHandle = { save: () => Promise<void> };

const COPY: Record<AddonKey, { label: string; noun: string; assignedHint: string }> = {
  investigations: { label: "Investigator Seats", noun: "investigator", assignedHint: "Seats are granted per user from the agency's user list." },
  video: { label: "Footage Analysis Seats", noun: "footage analysis", assignedHint: "Seats are granted per user from the agency's user list (Footage column). Each seat adds pooled footage hours for the department." },
};

const AddonSeatsSection = forwardRef<AddonSeatsSectionHandle, { agencyId: string; adminEmail: string; addon: AddonKey }>(({ agencyId, adminEmail, addon }, ref) => {
  const copy = COPY[addon];
  const [seats, setSeats] = useState<string>("0");
  const [source, setSource] = useState<Source>("invoice");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [interval, setInterval_] = useState<Interval>("month");
  const [periodStart, setPeriodStart] = useState<string>("");
  const [assigned, setAssigned] = useState<number>(0);
  const [billedByStripe, setBilledByStripe] = useState(false);
  const [original, setOriginal] = useState<string>("");
  const [pool, setPool] = useState<Pool | null>(null);
  const [creditHours, setCreditHours] = useState<string>("");
  const [creditSource, setCreditSource] = useState<"invoice" | "comp">("invoice");
  const [isAddingHours, setIsAddingHours] = useState(false);

  const snapshot = (s: string, src: string, end: string, iv: Interval, start: string) => `${s}|${src}|${end}|${iv}|${start}`;

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get<{
        seats: { seats_purchased: number; seats_source: string | null; billing_interval: Interval | null; current_period_start: string | null; current_period_end: string | null } | null;
        grants: unknown[];
        pool: Pool | null;
      }>(`/api/agency-management/agencies/${agencyId}/addons/${addon}`);
      setPool(data.pool ?? null);
      const n = String(data.seats?.seats_purchased ?? 0);
      // No row yet defaults to invoiced: that's what a hand-activated deal is.
      const raw = data.seats?.seats_source;
      const src: Source = raw === "trial" ? "trial" : raw === "comp" ? "comp" : "invoice";
      const end = data.seats?.current_period_end ? data.seats.current_period_end.slice(0, 10) : "";
      const iv: Interval = data.seats?.billing_interval === "year" ? "year" : "month";
      const start = data.seats?.current_period_start ? data.seats.current_period_start.slice(0, 10) : "";
      setSeats(n);
      setSource(src);
      setPeriodEnd(end);
      setInterval_(iv);
      setPeriodStart(start);
      setAssigned(data.grants?.length ?? 0);
      setBilledByStripe(raw === "stripe");
      setOriginal(snapshot(n, src, end, iv, start));
    } catch (error) {
      console.error(`Error fetching ${addon} seats:`, error);
    }
  }, [agencyId, addon]);

  useEffect(() => {
    load();
  }, [load]);

  useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        if (snapshot(seats, source, periodEnd, interval, periodStart) === original) return;
        const n = parseInt(seats, 10) || 0;
        if (n < 0 || n > 1000) throw new Error(`${copy.label} must be 0-1000`);
        await axios.patch(`/api/agency-management/agencies/${agencyId}/addons/${addon}/seats`, {
          adminEmail,
          seats: n,
          source,
          periodEnd: periodEnd || null,
          interval,
          periodStart: interval === "year" ? periodStart || null : null,
        });
        setOriginal(snapshot(seats, source, periodEnd, interval, periodStart));
        await load();
      },
    }),
    [seats, source, periodEnd, interval, periodStart, original, agencyId, addon, adminEmail, copy.label, load]
  );

  const handleAddHours = async () => {
    const h = parseInt(creditHours, 10);
    if (!h || h <= 0) return;
    setIsAddingHours(true);
    try {
      await axios.post(`/api/agency-management/agencies/${agencyId}/addons/${addon}/credits`, { adminEmail, hours: h, source: creditSource });
      setCreditHours("");
      await load();
    } catch (error) {
      console.error(`Error adding ${addon} hours:`, error);
      alert(axios.isAxiosError(error) && error.response ? error.response.data.error || "Failed to add hours" : "Failed to add hours");
    } finally {
      setIsAddingHours(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">{copy.label}</label>
      <div className="flex gap-2">
        <input
          type="number"
          min="0"
          max="1000"
          value={seats}
          onChange={(e) => setSeats(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
          }}
          className="input input-bordered w-28"
          placeholder="0"
        />
        <select value={source} onChange={(e) => setSource(e.target.value as Source)} className="select select-bordered flex-1">
          <option value="invoice">Paid (invoiced)</option>
          <option value="trial">Trial / evaluation</option>
          <option value="comp">Complimentary</option>
        </select>
        <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="input input-bordered w-44" title="Ends (optional)" />
      </div>
      <div className="flex gap-2 mt-2">
        <select value={interval} onChange={(e) => setInterval_(e.target.value as Interval)} className="select select-bordered select-sm flex-1" aria-label="Allowance period">
          <option value="month">Monthly allowance (resets each month)</option>
          <option value="year">Annual allowance (one pool for the billing year)</option>
        </select>
        {interval === "year" && (
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="input input-bordered input-sm w-44" title="Billing year starts (defaults to today)" />
        )}
      </div>
      {billedByStripe ? (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
          Billed through Stripe. Saving a change here overwrites the count and marks the seats as {source === "trial" ? "trial" : source === "comp" ? "complimentary" : "invoiced"}; change Stripe-billed seats in Stripe instead.
        </p>
      ) : (
        <p className="text-xs text-base-content/60 mt-1">
          {assigned} assigned. {copy.assignedHint} Leave the date blank for no expiry.
        </p>
      )}
      {pool && (
        <div className="mt-3 rounded-lg border border-base-300 bg-base-200 px-3 py-2">
          <p className="text-xs text-base-content/70 m-0">
            Pool for {pool.period.label}: <span className="font-semibold">{Math.round(pool.minutesUsed / 60)}</span> of {Math.round(pool.allowanceMinutes / 60)} hours used ({pool.pct}%)
            {pool.creditMinutes > 0 && <> · {Math.round(pool.creditMinutes / 60)} hours from packs</>}
          </p>
          <div className="flex gap-2 mt-2">
            <input
              type="number"
              min="1"
              max="10000"
              value={creditHours}
              onChange={(e) => setCreditHours(e.target.value.replace(/\D/g, ""))}
              className="input input-bordered input-sm w-28"
              placeholder="Hours"
              aria-label={`${copy.noun} hours to add`}
            />
            <select value={creditSource} onChange={(e) => setCreditSource(e.target.value as "invoice" | "comp")} className="select select-bordered select-sm flex-1">
              <option value="invoice">Paid (invoiced)</option>
              <option value="comp">Complimentary</option>
            </select>
            <Button variant="outline" size="sm" onClick={handleAddHours} disabled={isAddingHours || !creditHours} loading={isAddingHours}>
              Add hours
            </Button>
          </div>
          <p className="text-[11px] text-base-content/50 mt-1 m-0">Adds to the current {pool.period.interval === "year" ? "billing year" : "month"} only; packs don't roll over. Stripe-bought packs land here automatically.</p>
        </div>
      )}
    </div>
  );
});

AddonSeatsSection.displayName = "AddonSeatsSection";

export default AddonSeatsSection;
